import { z } from 'zod'

import type { TicketFinalOutcomeForQA } from '../../services/ticket/ticket'
import type { Customer, CustomerId } from '../customer/customer.dto'
import type { Segment } from '../segment/segment.dto'
import type { Subscriber, SubscriberId } from '../subscriber/subscriber.dto'
import type { ThirdPartyDataSourceId } from '../thirdPartyDataSource/thirdPartyDataSource.dto'
import type {
  TicketMessage,
  TicketMessageNestedUpsert,
} from '../ticketMessage/ticketMessage.dto'
import type { TicketOutcome } from '../ticketOutcome/ticketOutcome.dto'
import type { TicketTopic } from '../ticketTopic/ticketTopic.dto'
import type { User } from '../user/user.dto'
import type { WorkflowId } from '../workflow/workflowId.dto'

export const TicketIdSchema = z.string().brand('TicketId')
export type TicketId = z.infer<typeof TicketIdSchema>
export const MergeTicketIdSchema = z.string().brand('MergeTicketId')
export type MergeTicketId = z.infer<typeof MergeTicketIdSchema>
export const RemoteTicketIdSchema = z.string().brand('RemoteTicketId')
export type RemoteTicketId = z.infer<typeof RemoteTicketIdSchema>
export const RemoteTicketTokenSchema = z.string().brand('RemoteTicketToken')
export type RemoteTicketToken = z.infer<typeof RemoteTicketTokenSchema>
export const SegmentIdSchema = z.string().brand('SegmentId')
export type SegmentId = z.infer<typeof SegmentIdSchema>

/**
 * The inherent type and origin of a ticket.
 *
 * - 'Live': A real live ticket generated by an actual customer, handled via
 *  our email/batch pipeline.
 * - 'Test': A ticket generated for testing, handled via our email/batch
 *  pipeline.
 * - 'LiveInteractive': A real live ticket generated by an actual customer,
 *  handled via our live chat pipeline.
 * - 'TestInteractive': A ticket generated via conversation testing in the /conversation route of our app.
 * - 'Voice': A real live ticket generated by an actual customer, handled via our voice pipeline.
 * - 'TestVoice': A ticket generated for testing, handled via our voice pipeline.
 */
export type TicketType =
  | 'Live'
  | 'Test'
  | 'LiveInteractive'
  | 'TestInteractive'
  | 'Voice'
  | 'TestVoice'

/**
 * The modality of the ticket, affects choice of processing pipeline
 */
export type TicketChannel = 'chat' | 'email' | 'voice'
/**
 * How the ticket is being handled, one ticket channel can have multiple modes
 *
 * The `fastChat` mode is an experimental version of `chat` using simpler but
 * faster models. If it succeeds, it will replace `chat`.
 */
export type TicketMode = 'email' | 'chat' | 'voice' | 'fastChat'

/**
 * The status of the ticket in our system.
 *
 * - 'Unprocessed': We have not done anything to the ticket.
 * - 'Processing': The ticket is being processed. This includes cases where we are waiting for the customer to reply.
 * - 'Unhandled': Not enough workflows matched this ticket, so we did nothing.
 * - 'Triaged': We have triaged the ticket and assigned it to a human.
 * - 'DraftResponse': We have generated a draft response or other notes to help a human operator respond.
 * - 'Escalated': We tried to respond to the ticket, but had to escalate it to a human.
 * - 'EscalatedFailed': We tried to process the ticket, but something went wrong so a human needs to handle it.
 * - 'Responded': We have responded to the ticket.
 * - 'Processed': We have processed the ticket (taken some actions) but did not respond to the customer.
 * - 'Ignored': We have decided not to process the ticket, e.g. because it was closed in the remote system
 * - 'Error': A system error occurred while processing the ticket which
 * prevented us from completing our handling of it. This includes cases where we
 * tried to reassign the ticket but received an API error.
 * - 'Tagged': No other action was taken aside from ticket being tagged.
 */
export type InternalStatus =
  | 'Unprocessed'
  | 'Processing'
  | 'Unhandled'
  | 'Triaged'
  | 'DraftResponse'
  | 'Escalated'
  | 'EscalatedFailed'
  | 'Responded'
  | 'Processed'
  | 'Ignored'
  | 'Error'
  | 'Tagged'

export type RatingType = 'Good' | 'Bad' | 'Neutral' | 'Not rated'
export type CsatScore = 1 | 2 | 3 | 4 | 5

/**
 * A Ticket is an entire interaction between a customer and a subscriber.
 */
export class Ticket {
  /**
   * The unique ID of this ticket.
   */
  id: TicketId
  /**
   * The type of ticket this is.
   */
  type: TicketType
  /**
   * The customer that this ticket is with.
   */
  customerId: CustomerId
  customer?: Customer
  /**
   * The customer's email address.
   */
  customerEmail: string | undefined
  /**
   * The subscriber that this ticket is with.
   */
  subscriberId: SubscriberId
  subscriber?: Subscriber
  /**
   * ID of the ticket in the third-party system it was retrieved from.
   */
  remoteId?: RemoteTicketId
  /**
   * Token of the ticket in the third-party system it was retrieved from.
   * As of 19-09-2024, only used for Retell tickets.
   */
  remoteToken?: string
  /**
   * The segment that this ticket belongs to.
   */
  segment?: Segment
  segmentId?: SegmentId
  /**
   * If the message was retrieved from a third-party system via a Merge
   * integration as a Ticket, this is Merge.dev's ID for the Ticket.
   */
  mergeId?: MergeTicketId
  /**
   * The title of the ticket. If retrieved from Merge, this maps to Ticket.name.
   */
  title?: string
  /** Tags that have been applied to this ticket. */
  tags: string[]
  /**
   * The messages in this ticket, in date order, with most recent messages last.
   */
  messages: TicketMessage[]
  /**
   * The topics that have been raised in this ticket.
   */
  topics: TicketTopic[]
  /**
   * The third party source of the ticket
   */
  thirdPartyDataSourceId?: ThirdPartyDataSourceId
  /**
   * The user that this ticket is assigned to.
   */
  assigneeUserId?: string | null
  assigneeUser?: User
  /**
   * A user's feedback (rating and comments) about how a ticket was handled.
   */
  feedbackRating?: RatingType
  feedbackComments?: string[]
  feedbackCreatedAt?: Date
  /**
   * User provided Customer Satisfaction Score (CSAT) for the ticket.
   */
  csatScore?: CsatScore
  /**
   * User provided feedback on was the response helpful to the customer?
   * possibly shouuld live on ticket message
   */
  csatDidThatHelp?: boolean
  /** The current status of this ticket in our system. */
  internalStatus: InternalStatus
  /**
   * The date and time that this ticket was updated.
   */
  updatedAt: Date
  /**
   * The date and time that this ticket was created.
   */
  createdAt: Date
  /**
   * feedbackItem
   */
  ticketFeedback: (TicketOutcome & { workflowId?: string })[]
  /**
   * Use this flag to indicate that the ticket is potentially not a customer ticket
   * i.e not a ticket that should be counted as a ticket that we could have automated.
   * For example - spam emails.
   */
  isMaybeNotForAutomation: boolean
  attachments: { url: string; createdAt: Date; name: string; type: string }[]
  /** Optional workflow to run a test conversation */
  testWorkflowId?: WorkflowId
  /** The filepath of the voice recording for this ticket, if applicable. */
  voiceRecordingFilepath?: string
  /** The ticket that this ticket is a replay of, if applicable. */
  originalTicketId?: TicketId
}

export type TicketWithMatchedWorkflows = Ticket & {
  matchedWorkflowIds: WorkflowId[]
}

export type TicketWithFeedbackDetails = TicketWithMatchedWorkflows & {
  finalOutcome: TicketFinalOutcomeForQA
}

export type TicketUpsert = Omit<
  Ticket,
  | 'id'
  | 'createdAt'
  | 'messages'
  | 'topics'
  | 'customerEmail'
  | 'updatedAt'
  | 'internalStatus'
  | 'tags'
  | 'ticketFeedback'
  | 'isMaybeNotForAutomation'
  | 'feedbackRating'
  | 'feedbackComments'
  | 'customer'
  | 'attachments'
  | 'segment'
  // Don't let this be casually upserted. Segments should not change over the lifetime of a ticket.
  | 'segmentId'
> & {
  id?: TicketId
  messages: TicketMessageNestedUpsert[]
  tags?: string
  internalStatus?: InternalStatus
}
